import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AUTOMATION_TEMPLATE_UI } from '@/components/settings/automationTemplatesUi';
import { FacebookTemplateVariableMappingEditor } from '@/components/settings/FacebookTemplateVariableMappingEditor';
import {
    buildCampaignSourceOptions,
    buildFacebookFormSourceOptions,
    buildFacebookVariableMapping,
    countMetaTemplateBodyVariables,
    normalizeFacebookVariableMapping,
    normalizeFacebookVariableMappingFromApi,
    type FacebookLeadgenQuestion,
    type FacebookVariableMappingEntry,
    type MetaTemplateLike,
} from '@/components/settings/facebookTemplateMapping';

interface Campaign {
    id: string;
    name: string;
}

interface WABA {
    id: string;
    name: string;
}

interface Template extends MetaTemplateLike {
    status: string;
}

export interface CampaignLinkRecord {
    campaign_id: string;
    waba_id: string;
    template_name: string;
    template_language: string;
    variable_mapping?: FacebookVariableMappingEntry[];
    form_questions_snapshot?: FacebookLeadgenQuestion[];
}

interface CampaignLinkDialogProps {
    campaign: Campaign | null;
    adAccountId: string;
    existingLink?: CampaignLinkRecord | null;
    isOpen: boolean;
    onClose: () => void;
    onLinkSuccess: () => void;
}

function templateKey(template: MetaTemplateLike): string {
    return `${template.name}:${template.language}`;
}

export function CampaignLinkDialog({
    campaign,
    adAccountId,
    existingLink,
    isOpen,
    onClose,
    onLinkSuccess,
}: CampaignLinkDialogProps) {
    const { token } = useAuth();
    const [wabas, setWabas] = useState<WABA[]>([]);
    const [selectedWaba, setSelectedWaba] = useState<string>('');
    const [templates, setTemplates] = useState<Template[]>([]);
    const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('');
    const [variableMapping, setVariableMapping] = useState<FacebookVariableMappingEntry[]>([]);
    const [campaignFields, setCampaignFields] = useState<FacebookLeadgenQuestion[]>([]);
    const [loadingDialog, setLoadingDialog] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const sourceOptions = useMemo(
        () => (campaignFields.length > 0 ? buildFacebookFormSourceOptions(campaignFields) : buildCampaignSourceOptions()),
        [campaignFields],
    );

    const selectedTemplateObj = useMemo(
        () => templates.find((template) => templateKey(template) === selectedTemplateKey) ?? null,
        [templates, selectedTemplateKey],
    );

    const selectedTemplateBody = selectedTemplateObj?.components?.find(
        (component) => component.type?.toUpperCase() === 'BODY',
    )?.text;

    const fetchTemplates = useCallback(async (wabaId: string) => {
        setLoadingTemplates(true);
        try {
            const response = await fetch(`/api/v1/leads/meta/templates/?waba_id=${encodeURIComponent(wabaId)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('No se pudieron cargar las plantillas de Meta.');
            const data = await response.json();
            setTemplates(data.data || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'No se pudieron cargar las plantillas.');
            setTemplates([]);
        } finally {
            setLoadingTemplates(false);
        }
    }, [token]);

    const fetchWabas = useCallback(async () => {
        const response = await fetch('/api/v1/leads/meta/waba/', {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('No se pudieron cargar las cuentas de WhatsApp.');
        const data = await response.json();
        const businesses = data.data || [];
        const allWabas: WABA[] = [];
        interface Business {
            owned_whatsapp_business_accounts?: { data: WABA[] };
        }
        businesses.forEach((biz: Business) => {
            if (biz.owned_whatsapp_business_accounts?.data) {
                allWabas.push(...biz.owned_whatsapp_business_accounts.data);
            }
        });
        setWabas(allWabas);
        return allWabas;
    }, [token]);

    useEffect(() => {
        if (!isOpen || !campaign) return;

        let cancelled = false;

        const load = async () => {
            setLoadingDialog(true);
            setError(null);
            setSelectedWaba('');
            setSelectedTemplateKey('');
            setTemplates([]);
            setVariableMapping([]);
            setCampaignFields([]);

            try {
                await fetchWabas();
                if (cancelled) return;

                let fields: FacebookLeadgenQuestion[] = [];
                if (campaign.id) {
                    const response = await fetch(
                        `/api/v1/leads/meta/campaign-fields/?campaign_id=${encodeURIComponent(campaign.id)}`,
                        { headers: { Authorization: `Bearer ${token}` } },
                    );
                    if (response.ok) {
                        const data = await response.json();
                        fields = Array.isArray(data?.questions) ? data.questions : [];
                    }
                }
                if (fields.length === 0 && existingLink?.form_questions_snapshot?.length) {
                    fields = existingLink.form_questions_snapshot;
                }
                if (!cancelled) setCampaignFields(fields);

                if (existingLink?.waba_id) {
                    setSelectedWaba(existingLink.waba_id);
                    await fetchTemplates(existingLink.waba_id);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Error al cargar la configuración.');
                }
            } finally {
                if (!cancelled) setLoadingDialog(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [isOpen, campaign, existingLink, fetchWabas, fetchTemplates, token]);

    useEffect(() => {
        if (!isOpen || !existingLink || !templates.length) return;
        const key = `${existingLink.template_name}:${existingLink.template_language}`;
        const template = templates.find((item) => templateKey(item) === key);
        if (!template) return;
        const count = countMetaTemplateBodyVariables(template);
        const previous = normalizeFacebookVariableMappingFromApi(existingLink.variable_mapping);
        setSelectedTemplateKey(key);
        setVariableMapping(buildFacebookVariableMapping(count, previous));
    }, [isOpen, existingLink, templates]);

    useEffect(() => {
        if (!selectedTemplateObj || existingLink) return;
        const count = countMetaTemplateBodyVariables(selectedTemplateObj);
        setVariableMapping((previous) => buildFacebookVariableMapping(count, previous));
    }, [selectedTemplateObj, existingLink]);

    const handleWabaChange = async (value: string) => {
        setSelectedWaba(value);
        setSelectedTemplateKey('');
        setVariableMapping([]);
        await fetchTemplates(value);
    };

    const handleTemplateChange = (value: string) => {
        setSelectedTemplateKey(value);
        const template = templates.find((item) => templateKey(item) === value);
        if (!template) {
            setVariableMapping([]);
            return;
        }
        const count = countMetaTemplateBodyVariables(template);
        setVariableMapping((previous) => buildFacebookVariableMapping(count, previous));
    };

    const handleSave = async () => {
        if (!campaign || !selectedWaba || !selectedTemplateKey || !selectedTemplateObj) return;

        const normalizedMapping = normalizeFacebookVariableMapping(variableMapping);
        if (variableMapping.length > 0 && normalizedMapping.length !== variableMapping.length) {
            setError('Completa el mapeo de todas las variables de la plantilla.');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const payload: Record<string, unknown> = {
                campaign_id: campaign.id,
                campaign_name: campaign.name,
                ad_account_id: adAccountId,
                waba_id: selectedWaba,
                template_name: selectedTemplateObj.name,
                template_language: selectedTemplateObj.language,
            };

            if (normalizedMapping.length > 0) {
                payload.variable_mapping = normalizedMapping;
            }
            const snapshot = campaignFields.length > 0 ? campaignFields : existingLink?.form_questions_snapshot ?? [];
            if (snapshot.length > 0) {
                payload.form_questions_snapshot = snapshot;
            }

            const response = await fetch('/api/v1/leads/meta/links/', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const mappingError = Array.isArray(errorData.variable_mapping) ? errorData.variable_mapping[0] : null;
                throw new Error(mappingError || errorData.error || 'No se pudo guardar la configuración.');
            }

            onLinkSuccess();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'No se pudo guardar la configuración.');
        } finally {
            setSubmitting(false);
        }
    };

    const isEditing = Boolean(existingLink);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? AUTOMATION_TEMPLATE_UI.editTemplateButton : 'Configurar plantilla de campaña'}
                    </DialogTitle>
                </DialogHeader>

                {loadingDialog ? (
                    <div className="flex items-center gap-2 py-8 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Cargando configuración…
                    </div>
                ) : (
                    <div className="space-y-4">
                        {error ? (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ) : null}

                        <div className="space-y-2">
                            <Label>Campaña</Label>
                            <div className="rounded-md border bg-muted p-2 text-sm">
                                {campaign?.name}
                                <div className="text-xs text-muted-foreground">{campaign?.id}</div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Cuenta de WhatsApp</Label>
                            <Select value={selectedWaba || undefined} onValueChange={(value) => void handleWabaChange(value)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar WABA" />
                                </SelectTrigger>
                                <SelectContent>
                                    {wabas.map((waba) => (
                                        <SelectItem key={waba.id} value={waba.id}>
                                            {waba.name || waba.id}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Plantilla aprobada de Meta</Label>
                            <Select
                                value={selectedTemplateKey || undefined}
                                onValueChange={handleTemplateChange}
                                disabled={!selectedWaba || loadingTemplates}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar plantilla" />
                                </SelectTrigger>
                                <SelectContent>
                                    {templates.map((template) => (
                                        <SelectItem key={templateKey(template)} value={templateKey(template)}>
                                            {template.name} ({template.language}) — {countMetaTemplateBodyVariables(template)} vars
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedTemplateBody ? (
                                <p className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground whitespace-pre-wrap">
                                    {selectedTemplateBody}
                                </p>
                            ) : null}
                        </div>

                        {selectedTemplateKey ? (
                            <FacebookTemplateVariableMappingEditor
                                mapping={variableMapping}
                                sourceOptions={sourceOptions}
                                onChange={setVariableMapping}
                                showHelp
                                description={
                                    campaignFields.length > 0
                                        ? 'Asigna cada hueco a un campo del formulario usado en esta campaña, al lead o al contexto de Meta.'
                                        : 'Esta campaña no expone campos de formulario. Usa datos del lead o contexto de Meta.'
                                }
                            />
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                Elige una plantilla para mapear sus variables.
                            </p>
                        )}
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        disabled={submitting || loadingDialog || !selectedTemplateKey}
                        onClick={() => void handleSave()}
                    >
                        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Guardar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
